// Contexto de Autenticación para la aplicación Amarsi Control Panel
// Maneja el estado global de autenticación y proporciona métodos para login/logout

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuthMutations } from '../hooks/useAuthMutations';
import { useAuthQuery } from '../hooks/useAuthQuery';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import toast from 'react-hot-toast';

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

// Variable global para rastrear el último refresh de token (previene refreshs duplicados por re-montue)
let globalLastRefreshTime = 0;

// Creación del contexto
const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Provider del contexto de autenticación
export const AuthProvider = ({ children }) => {
  // Estado local para el usuario y sesión
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hooks personalizados para mutaciones, queries y manejo de tokens
  const { loginMutation, logoutMutation, registerMutation } = useAuthMutations();
  const { sessionQuery } = useAuthQuery();
  const { ensureValidToken, isRefreshing } = useTokenRefresh();

  // Efecto para sincronizar el estado local con React Query y validar tokens
  useEffect(() => {
    const syncSession = async () => {
      // Si ya hay datos de sesión, los usamos inmediatamente
      if (sessionQuery.data?.session) {
        // Previene refreshs innecesarios en corto tiempo (mínimo 30 segundos, usando variable global)
        const now = Date.now();
        if (now - globalLastRefreshTime < 30000) {
          if (DEBUG) console.log('AuthContext: Refresh muy reciente, usando sesión actual');
          setUser(sessionQuery.data.user);
          setSession(sessionQuery.data.session);
          setLoading(false);
          return;
        }

        // Verificamos si necesitamos renovar el token (solo si está próximo a expirar)
        const validSession = await ensureValidToken(sessionQuery.data.session);

        if (validSession && validSession !== sessionQuery.data.session) {
          // Solo actualizamos si la sesión realmente cambió
          if (DEBUG) console.log('AuthContext: Sesión actualizada después de refresh');
          globalLastRefreshTime = now;
          setUser(validSession.user || validSession?.user);
          setSession(validSession);
        } else if (!validSession) {
          // Si falló la renovación, usamos la sesión original
          if (DEBUG) console.log('AuthContext: Falló renovación, usando sesión original');
          setUser(sessionQuery.data.user);
          setSession(sessionQuery.data.session);
        } else {
          // Si no se necesitó renovar, usamos los datos originales
          setUser(sessionQuery.data.user);
          setSession(sessionQuery.data.session);
        }
        setLoading(false);
      } else if (!sessionQuery.isLoading) {
        // Solo ponemos loading en false si la query ya terminó de cargar
        setUser(null);
        setSession(null);
        setLoading(false);
      }
    };

    syncSession();
  }, [sessionQuery.data, sessionQuery.isLoading, ensureValidToken]);

  // Efecto para escuchar cambios en la autenticación de Supabase
  useEffect(() => {
    // Escucha eventos de autenticación en tiempo real
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // console.log('Evento de autenticación:', event, newSession);
        
        // Optimización: Solo refrescamos la query para eventos críticos
        // Evitamos refrescar en TOKEN_REFRESHED para prevenir llamadas innecesarias
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          sessionQuery.refetch();
        }
        
        // Muestra notificaciones según el evento
        switch (event) {
          case 'SIGNED_IN':
            toast.success('¡Bienvenido de nuevo!');
            break;
          case 'SIGNED_OUT':
            toast.success('Sesión cerrada correctamente');
            break;
          case 'TOKEN_REFRESHED':
            // Evento silencioso - no mostramos notificación ni refrescamos
            // El token se actualiza automáticamente en segundo plano
            break;
          default:
            break;
        }
      }
    );

    // Limpieza del listener al desmontar
    return () => subscription.unsubscribe();
  }, [sessionQuery]);

  // Función de login utilizando la mutación de React Query
  const login = async (email, password, rememberMe = false) => {
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      return result;
    } catch (error) {
      // El error ya es manejado por la mutación, solo lo propagamos
      throw error;
    }
  };

  // Función de logout utilizando la mutación de React Query
  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // El error ya es manejado por la mutación
      throw error;
    }
  };

  // Función de registro utilizando la mutación de React Query
  const register = async (email, password, userData) => {
    try {
      const result = await registerMutation.mutateAsync({ email, password, userData });
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Verificación si el usuario está autenticado
  const isAuthenticated = !!user && !!session;

  // Objeto de valor del contexto
  const value = {
    // Estado
    user,
    session,
    // No incluir isRefreshing en loading para evitar re-montue de componentes durante refresh silencioso
    loading: loading || sessionQuery.isLoading,
    isAuthenticated,
    
    // Estados de carga de mutaciones
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRegistering: registerMutation.isPending,
    
    // Estados de renovación
    isRefreshingToken: isRefreshing,
    
    // Métodos
    login,
    logout,
    register,
    
    // Refrescar sesión manualmente
    refetchSession: sessionQuery.refetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
